import { FC, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useConfig } from '../../../hooks/UseConfig';
import { useOutsideClickDetection } from '../../../hooks/UseOutsideClickDetection';
import { logoutRedirect } from '../../../services/auth/AuthService';
import { HeaderService } from '../../../services/shared/HeaderService';
import { currentYear } from '../../../services/utils';
import { AppState } from '../../../store/AppState';
import { CurrentUser } from '../../../types';
import { DealerState } from '../../../types/DealerState';
import { IF } from '../../shared/generics';
import SupportHelp from '../support-help/SupportHelp';
import UserProfileModal from '../user-profile/user-profile-modal/UserProfileModal';
import SelectStoreModal from './SelectStoreModal';

export const UserMenuHeader: FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectStoreModalIsOpen, setSelectStoreModalIsOpen] = useState<boolean>(false);
  const [userProfileModalIsOpen, setUserProfileModalIsOpen] = useState<boolean>(false);
  const [supportModalIsOpen, setSupportModalIsOpen] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const storeCode = localStorage.getItem('am.storeCode');
  const storeName = localStorage.getItem('am.storeName');
  const singleActiveStore = localStorage.getItem('am.singleActiveStore') === 'true';

  const { thoughtIndustriesUrl } = useConfig();
  const { dealerInfos } = useSelector<AppState, DealerState>((state) => state.dealer);

  const currentUser = useSelector<AppState, CurrentUser>((state) => state.currentUser);

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const handleOnUserNameClick = (): void => {
    setIsVisible(!isVisible);
  };

  const handleOnStoreNameClick = (): void => {
    setSelectStoreModalIsOpen(true);
    setIsVisible(!isVisible);
  };

  const openUserProfileModal = (): void => {
    setUserProfileModalIsOpen(true);
    setIsVisible(!isVisible);
  };

  const fetchThoughtIndustriesJwt = async () => {
    const title = currentUser.jobTitle != null ? currentUser.jobTitle : 'N/A';
    await HeaderService.getThoughtIndustriesJwt(
      dealerInfos[0].dealerId,
      dealerInfos[0].name,
      title,
    ).then((response) => {
      window.open(`${thoughtIndustriesUrl}/access/jwt?jwt=${response.jwt}`);
    });
  };

  const openThoughtIndustries = (): void => {
    fetchThoughtIndustriesJwt();
    setIsVisible(!isVisible);
  };

  const openHelp = (): void => {
    setSupportModalIsOpen(true);
    setIsVisible(!isVisible);
  };

  const logout = (): void => {
    setIsVisible(!isVisible);
    logoutRedirect();
  };

  return (
    <div ref={wrapperRef} data-testid='user-menu-header'>
      <a
        onClick={handleOnUserNameClick}
        className='navbar-menu'
        title={
          currentUser.shadowUserFirstName ? currentUser.shadowUserFirstName : currentUser.firstName
        }
      >
        <i className='am-icon am-icon-hamburger user-menu-sidebar'></i>
        <span id='user'>
          {currentUser.shadowUserFirstName
            ? currentUser.shadowUserFirstName
            : currentUser.firstName}
        </span>
      </a>
      <div className={`user-menu-panel ${isVisible ? 'open' : ''}`} data-testid='user-menu-panel'>
        <div className='user-menu-header' onClick={handleOnUserNameClick}>
          <i className='am-icon am-icon-hamburger'></i>
          <span className='user-menu-name'>
            {currentUser.shadowUserFirstName ?? currentUser.firstName}{' '}
            {currentUser.shadowUserLastName ?? currentUser.lastName}
          </span>
          <i className='am-icon am-icon-x close'></i>
        </div>
        <ul>
          <IF condition={Boolean(currentUser.isPrimaryUser) && Boolean(!currentUser.isShadowLogin)}>
            <li>
              <a onClick={handleOnStoreNameClick}>
                <span className='am-icon am-icon-new-car'></span>Store: {storeCode}
                <div>{storeName}</div>
              </a>
            </li>
          </IF>
          <IF
            condition={
              !currentUser.isPrimaryUser && currentUser.isMultiStoreUser && !singleActiveStore
            }
          >
            <li>
              <a className='storename' onClick={handleOnStoreNameClick}>
                <div className='user-menu-dealername'>{storeName}</div>
                <div className='user-menu-change'>CHANGE</div>
              </a>
            </li>
          </IF>
          <li>
            <a onClick={openUserProfileModal}>
              <span className='am-icon am-icon-profile'></span>Your Profile
            </a>
          </li>
          <li>
            <a onClick={openThoughtIndustries}>
              <span className='am-icon am-icon-thought'></span>Mastermind Academy
            </a>
          </li>
          <li>
            <a onClick={openHelp}>
              <span className='am-icon am-icon-help'></span>Support
            </a>
          </li>
          <li>
            <a onClick={logout}>
              <span className='am-icon am-icon-logout'></span>Log Out
            </a>
          </li>
        </ul>
        <div className='user-menu-footer' data-testid='user-menu-footer'>
          <p>
            <a
              href='https://www.automotivemastermind.com/terms-of-service/'
              onClick={() => setIsVisible(false)}
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms of Service
            </a>
            &nbsp;|&nbsp;
            <a
              href='https://www.automotivemastermind.com/privacy-policy/'
              onClick={() => setIsVisible(false)}
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy Policy
            </a>
          </p>
          <p>
            <span>
              &copy; {currentYear()} automotiveMastermind&reg;.
              <br />
              All rights reserved.
            </span>
          </p>
          <div className='am-logo'></div>
        </div>
      </div>
      {supportModalIsOpen && <SupportHelp setIsOpen={setSupportModalIsOpen} />}
      <SelectStoreModal
        modalIsOpen={selectStoreModalIsOpen}
        setIsOpen={setSelectStoreModalIsOpen}
      />
      <UserProfileModal
        modalIsOpen={userProfileModalIsOpen}
        setIsOpen={setUserProfileModalIsOpen}
        data-testid='user-profile-modal'
      />
    </div>
  );
};

export default UserMenuHeader;
